import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Menu, X, Heart, User, LogOut, LayoutDashboard } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const links = [
  { to: "/", label: "Stays" },
  { to: "/search?location=Goa", label: "Goa" },
  { to: "/search?location=Bangalore", label: "Bangalore" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header className="glass-nav sticky top-0 z-50" data-testid="site-navbar">
      <div className="mx-auto max-w-7xl px-6 md:px-10 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
          <span className="font-serif-display text-2xl tracking-tight" style={{ color: "hsl(165 37% 17%)" }}>New Day</span>
          <span className="font-serif-display text-2xl italic" style={{ color: "hsl(10 51% 55%)" }}>Stays</span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-sm font-medium tracking-wide transition-colors ${
                  isActive ? "text-[hsl(10_51%_55%)]" : "text-[hsl(90_4%_10%)] hover:text-[hsl(10_51%_55%)]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {user && user !== false ? (
            <>
              <Link to="/wishlist" data-testid="nav-wishlist">
                <Button variant="ghost" size="sm" className="text-[hsl(90_4%_10%)]">
                  <Heart className="h-4 w-4 mr-2" /> Wishlist
                </Button>
              </Link>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="nav-user-menu" className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-[hsl(165_37%_17%/0.15)] hover:bg-white transition-colors">
                    <User className="h-4 w-4" />
                    <span className="text-sm">{user.name?.split(" ")[0]}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  <DropdownMenuItem asChild>
                    <Link to="/my-bookings" data-testid="menu-my-bookings">My bookings</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/wishlist" data-testid="menu-wishlist">Wishlist</Link>
                  </DropdownMenuItem>
                  {user.role === "admin" && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem asChild>
                        <Link to="/admin" data-testid="menu-admin">
                          <LayoutDashboard className="h-4 w-4 mr-2" /> Admin dashboard
                        </Link>
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout">
                    <LogOut className="h-4 w-4 mr-2" /> Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login">
                <Button variant="ghost" size="sm">Sign in</Button>
              </Link>
              <Link to="/register" data-testid="nav-register">
                <Button size="sm" className="btn-forest rounded-full px-5">Join</Button>
              </Link>
            </>
          )}
        </div>

        <button
          className="md:hidden p-2"
          onClick={() => setOpen((o) => !o)}
          data-testid="nav-mobile-toggle"
          aria-label="Menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-[hsl(165_37%_17%/0.08)] bg-[hsl(42_30%_96%)]">
          <div className="px-6 py-4 space-y-3">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                onClick={() => setOpen(false)}
                className="block py-2 text-base font-medium"
                data-testid={`mobile-link-${l.label.toLowerCase()}`}
              >
                {l.label}
              </NavLink>
            ))}
            <div className="pt-3 border-t border-[hsl(165_37%_17%/0.08)] flex gap-3">
              {user && user !== false ? (
                <>
                  <Link to="/my-bookings" onClick={() => setOpen(false)} className="flex-1">
                    <Button variant="outline" className="w-full" data-testid="mobile-my-bookings">My bookings</Button>
                  </Link>
                  <Button onClick={handleLogout} className="flex-1 btn-forest" data-testid="mobile-logout">Log out</Button>
                </>
              ) : (
                <>
                  <Link to="/login" onClick={() => setOpen(false)} className="flex-1">
                    <Button variant="outline" className="w-full" data-testid="mobile-login">Sign in</Button>
                  </Link>
                  <Link to="/register" onClick={() => setOpen(false)} className="flex-1">
                    <Button className="w-full btn-forest" data-testid="mobile-register">Join</Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
